import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "../../../Catalyst/dialog";
import { Button } from "../../../Catalyst/button";
import { InviteCsvSection } from "./InviteCsvSection";
import { InviteManualSection } from "./InviteManualSection";
import FinleyIconName from "../../../../assets/FinleyIconName";
import { AppDispatch, RootState } from "../../../../store/store";
import { STATUS } from "../../../../store/slices/OrganizationSlice";
import { fetchAccessibleOrganizationsAsync } from "../../../../store/thunks/UsersV3Thunk";
import { getInvitableRoleOptions } from "../../../../constants/users";
import { getOrganizationName } from "../../Organizations/utils";

interface OrgRoleInviteDialogProps {
  open: boolean;
  onClose: () => void;
  // Preselects the organization on both invite paths, e.g. when opened from an
  // organization's page; the admin can still pick another one.
  defaultOrganizationId?: number | null;
  // Called after users are invited so the caller can refresh its list.
  onInvited?: () => void;
}

// Invite users via CSV upload or a manual form, picking the target organization
// and role on each path (ADM-92). The options are limited to the orgs the admin
// can see (GET /v3/organizations/accessible) and the roles they may grant. Used
// by the global user list (/users); the original org-scoped invite
// (common/InviteUsersDialog) stays on the user management dashboard and the
// Organizations screen until this flow is fully rolled out.
const OrgRoleInviteDialog = ({ open, onClose, defaultOrganizationId = null, onInvited }: OrgRoleInviteDialogProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const userRole = useSelector((state: RootState) => state.OrganizationListStore?.userAccount?.user_role);
  const { organizationOptions, organizationOptionsStatus } = useSelector(
    (state: RootState) => state.UsersV3Store
  );

  // Bumped on every open so both forms start fresh (and pick up the default org).
  const [session, setSession] = useState(0);
  const [csvOrganizationId, setCsvOrganizationId] = useState<number | null>(defaultOrganizationId);

  useEffect(() => {
    if (!open) return;
    setSession((value) => value + 1);
    setCsvOrganizationId(defaultOrganizationId);
  }, [open, defaultOrganizationId]);

  // Load the org options when needed (the global user list may already have them);
  // retry after a failure each time the dialog opens.
  useEffect(() => {
    if (
      open &&
      (organizationOptionsStatus === STATUS.IDLE || organizationOptionsStatus === STATUS.FAILED)
    ) {
      dispatch(fetchAccessibleOrganizationsAsync());
    }
  }, [open, organizationOptionsStatus, dispatch]);

  const roleOptions = useMemo(() => getInvitableRoleOptions(userRole), [userRole]);

  const csvOrganization = organizationOptions.find((org) => org.id === csvOrganizationId);
  const title = csvOrganization
    ? `Invite users to ${getOrganizationName(csvOrganization)} with CSV Upload`
    : "Invite users with CSV Upload";

  const optionProps = {
    organizationOptions,
    isOrganizationOptionsLoading: organizationOptionsStatus === STATUS.LOADING,
    isOrganizationOptionsError: organizationOptionsStatus === STATUS.FAILED,
    roleOptions,
    onInvited,
  };

  return (
    <Dialog size="5xl" open={open} onClose={() => {}}>
      <div className="mt-[25px] flex w-full items-center">
        <div className="w-full rounded-lg bg-white shadow-sm">
          <div className="mb-6">
            <Button outline className="h-10 w-18" onClick={onClose}>
              Close
            </Button>
          </div>

          <div className="mb-4 flex items-center gap-2">
            <FinleyIconName />
          </div>

          <h2 className="mb-4 desktext14 text-gray-900">{title}</h2>

          <InviteCsvSection
            key={`csv-${session}`}
            organizationId={csvOrganizationId}
            onOrganizationChange={setCsvOrganizationId}
            {...optionProps}
          />

          <div className="my-6 text-start text-sm text-gray-500">OR</div>

          <InviteManualSection
            key={`manual-${session}`}
            defaultOrganizationId={defaultOrganizationId}
            {...optionProps}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default OrgRoleInviteDialog;
